import React, { useMemo, useState } from 'react'
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, IconButton, InputAdornment, List, ListItem, ListItemText,
    TextField, Tooltip, Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

/**
 * Gerência da seleção atual.
 *
 * Existe porque a seleção sobrevive à paginação: quem marcou 5 hosts em 3
 * páginas diferentes não tem, na tabela, onde ver os 5 juntos nem como tirar
 * um do meio sem voltar até a página dele. Aqui a lista é a seleção inteira,
 * independente de onde cada host estava quando foi marcado.
 *
 * As ações em massa ficam neste mesmo diálogo (via `children`) porque é aqui
 * que a pessoa acaba de conferir o que vai ser afetado — mandar ela fechar
 * pra clicar no botão lá fora só aumenta a chance de aplicar na lista errada.
 */
export default function SelecaoHostsDialog({
    open, onClose, itens = [], onRemover, onLimpar, children
}) {
    const [busca, setBusca] = useState('')

    const filtrados = useMemo(() => {
        const termo = busca.trim().toLowerCase()
        if (!termo) return itens
        return itens.filter((i) =>
            String(i.nome || '').toLowerCase().includes(termo) ||
            String(i.ip || '').toLowerCase().includes(termo))
    }, [itens, busca])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            // Mesmo motivo do BulkHostDialog: background.paper do tema é escuro.
            PaperProps={{ sx: { bgcolor: 'background.default', color: 'text.primary' } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <span>Hosts selecionados</span>
                    <Chip size="small" label={itens.length} color="primary" variant="outlined" />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
                {itens.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nenhum host selecionado.
                    </Typography>
                ) : (
                    <>
                        {itens.length > 8 && (
                            <TextField
                                fullWidth size="small" placeholder="Filtrar por nome ou IP"
                                value={busca} onChange={(e) => setBusca(e.target.value)}
                                sx={{ mb: 1.5, mt: 1 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        )}

                        <List dense sx={{ maxHeight: 340, overflowY: 'auto', py: 0 }}>
                            {filtrados.map((item) => (
                                <ListItem
                                    key={item.id}
                                    divider
                                    secondaryAction={
                                        <Tooltip title="Tirar da seleção">
                                            <IconButton edge="end" size="small"
                                                onClick={() => onRemover(item.id)}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    <ListItemText
                                        primary={item.nome || `Host ${item.id}`}
                                        secondary={item.ip}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                        secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                </ListItem>
                            ))}
                            {filtrados.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    Nenhum selecionado corresponde a "{busca}".
                                </Typography>
                            )}
                        </List>

                        <Divider sx={{ mt: 1.5 }} />
                        <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 1.5 }}>
                            Para adicionar hosts, marque as linhas na tabela — a seleção não se
                            perde ao trocar de página ou de filtro.
                        </Typography>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                <Button onClick={onLimpar} color="inherit" disabled={itens.length === 0}
                    sx={{ textTransform: 'none' }}>
                    Limpar seleção
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {children}
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}
